/**
 * Terms.jsx — EVORA Terms of Service Page
 *
 * Layout architecture:
 *   .terms-layout  → full-bleed 100vw wrapper (background, vertical padding)
 *   .terms-page    → max-width 900px centred content shell (readability)
 *   .terms-hero    → bleeds full-width via negative margins in CSS
 *   .terms-cta     → bleeds full-width via negative margins in CSS
 */

import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/common/Layout/Layout';
import { BRAND } from '../../config/brand';
import { IconFileText, IconArrowRight } from '../../components/common/Icons/Icons';
import './Terms.css';

// ── Constants ──────────────────────────────────────────────────────────────────
const SITE_URL      = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const BRAND_NAME    = BRAND?.name || 'EVORA';
const TERMS_URL     = `${SITE_URL}/terms`;
const OG_IMAGE      = `${SITE_URL}/og-image.jpg`;
const CONTACT_EMAIL = BRAND?.email || 'legal@evoraclothing.com';
const ISO_DATE      = '2026-01-01';
const LAST_UPDATED  = 'January 1, 2026';

// ── JSON-LD — serialised once at module load, never inside render ──────────────
const SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${TERMS_URL}#webpage`,
      url: TERMS_URL,
      name: `Terms of Service | ${BRAND_NAME}`,
      description: `Terms and conditions for using ${BRAND_NAME}'s website, making purchases, and accessing our services.`,
      inLanguage: 'en-IN',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: { '@id': `${TERMS_URL}#breadcrumb` },
      dateModified: ISO_DATE,
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${TERMS_URL}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Terms of Service', item: TERMS_URL },
      ],
    },
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: BRAND_NAME,
      url: SITE_URL,
      contactPoint: {
        '@type': 'ContactPoint',
        email: CONTACT_EMAIL,
        contactType: 'legal',
        areaServed: 'IN',
      },
    },
  ],
});

// ── Section data — keep copy out of JSX for easy legal updates ────────────────
const SECTIONS = [
  {
    id: 'agreement',
    heading: '1. Agreement to Terms',
    content: (
      <>
        <p>
          These Terms of Service ("Terms") are a legally binding agreement between you
          and {BRAND_NAME} ("we," "our," or "us") governing your use of our website,
          services, and products. By accessing or using our website you agree to be
          bound by these Terms and our Privacy Policy.
        </p>
        <p>
          If you do not agree, you may not access our services. Modifications take
          effect immediately upon posting. Continued use after changes constitutes
          acceptance.
        </p>
      </>
    ),
  },
  {
    id: 'eligibility',
    heading: '2. Eligibility and Account Registration',
    content: (
      <>
        <h3 className="terms-section__subtitle">2.1 Age Requirement</h3>
        <p>
          You must be at least 18 years old to use our services. By using our website
          you represent that you have the legal capacity to enter into these Terms.
        </p>
        <h3 className="terms-section__subtitle">2.2 Account Registration</h3>
        <p>You agree to:</p>
        <ul className="terms-list">
          <li>Provide accurate, current, and complete information during registration</li>
          <li>Keep your account information up to date</li>
          <li>Maintain the security of your password and account</li>
          <li>Accept responsibility for all activity that occurs under your account</li>
          <li>Notify us immediately of any unauthorised use of your account</li>
        </ul>
      </>
    ),
  },
  {
    id: 'products',
    heading: '3. Products, Pricing, and Availability',
    content: (
      <>
        <h3 className="terms-section__subtitle">3.1 Product Information</h3>
        <p>
          We strive to provide accurate product descriptions and images but do not
          warrant that content is error-free or complete. Colour representation may
          vary by device display.
        </p>
        <h3 className="terms-section__subtitle">3.2 Pricing</h3>
        <p>
          All prices are in Indian Rupees (₹) and subject to change without notice.
          Prices exclude applicable taxes and shipping unless explicitly stated. We
          reserve the right to correct pricing errors even after an order is placed.
        </p>
        <h3 className="terms-section__subtitle">3.3 Availability</h3>
        <p>
          We may limit quantities, discontinue products, or refuse orders at our
          discretion. If a product becomes unavailable after your order we will notify
          you and issue a full refund.
        </p>
      </>
    ),
  },
  {
    id: 'orders',
    heading: '4. Orders and Payment',
    content: (
      <>
        <h3 className="terms-section__subtitle">4.1 Order Acceptance</h3>
        <p>
          Your order is an offer to purchase. We may reject any order for reasons
          including unavailability, pricing errors, or suspected fraudulent activity.
          If rejected, we will notify you and issue a full refund.
        </p>
        <h3 className="terms-section__subtitle">4.2 Payment Methods</h3>
        <ul className="terms-list">
          <li>Credit and debit cards — Visa, Mastercard, RuPay</li>
          <li>UPI (Unified Payments Interface)</li>
          <li>Net banking</li>
          <li>Digital wallets</li>
          <li>Prepaid only — UPI, cards, and net banking</li>
        </ul>
        <p>Payment must be received in full before we process and ship your order.</p>
        <h3 className="terms-section__subtitle">4.3 Order Confirmation</h3>
        <p>
          Order acceptance occurs when we dispatch your item and send a shipping
          confirmation — not at the acknowledgement email stage.
        </p>
      </>
    ),
  },
  {
    id: 'shipping',
    heading: '5. Shipping and Delivery',
    content: (
      <>
        <p>
          Full details are in our{' '}
          <Link to="/shipping" className="terms-link">Shipping &amp; Delivery Policy</Link>.
          By placing an order you agree to those terms, including:
        </p>
        <ul className="terms-list">
          <li>Estimated delivery of 7–10 business days within India</li>
          <li>Free shipping on all orders</li>
          <li>Delivery restricted to serviceable pin codes</li>
          <li>Transfer of risk upon delivery to the address provided</li>
        </ul>
        <p>
          We are not responsible for delays caused by carriers, natural disasters, or
          other circumstances beyond our reasonable control.
        </p>
      </>
    ),
  },
  {
    id: 'returns',
    heading: '6. Returns, Exchanges, and Refunds',
    content: (
      <>
        <p>
          Full details are in our{' '}
          <Link to="/returns" className="terms-link">Returns &amp; Exchanges Policy</Link>.
          Key points:
        </p>
        <ul className="terms-list">
          <li>Returns accepted for size issues and defective items only</li>
          <li>7-day return window from date of delivery</li>
          <li>Items must be unworn, unwashed, with all original tags attached</li>
          <li>Refunds processed within 7–10 business days of receiving the return</li>
          <li>Original shipping fees are non-refundable unless the item is defective</li>
        </ul>
        <p>We reserve the right to refuse returns that do not meet these requirements.</p>
      </>
    ),
  },
  {
    id: 'ip',
    heading: '7. Intellectual Property Rights',
    content: (
      <>
        <p>
          All website content — text, graphics, logos, images, and software — is the
          property of {BRAND_NAME} or its licensors and is protected under Indian and
          international intellectual property law.
        </p>
        <p>
          You may not reproduce, distribute, or modify any content without our prior
          written consent.
        </p>
      </>
    ),
  },
  {
    id: 'conduct',
    heading: '8. User Conduct and Prohibited Activities',
    content: (
      <>
        <p>You agree not to:</p>
        <ul className="terms-list">
          <li>Use our website for any unlawful purpose or in violation of applicable law</li>
          <li>Impersonate any person or entity</li>
          <li>Interfere with or disrupt our website, servers, or networks</li>
          <li>Attempt unauthorised access to any part of our systems</li>
          <li>Use automated tools (bots, scrapers) without our express permission</li>
          <li>Transmit viruses, malware, or other harmful code</li>
          <li>Collect information about other users without their consent</li>
        </ul>
      </>
    ),
  },
  {
    id: 'disclaimers',
    heading: '9. Disclaimers and Limitations of Liability',
    content: (
      <>
        <h3 className="terms-section__subtitle">9.1 Product Disclaimers</h3>
        <p>
          Products are provided "as is" without warranties of any kind, express or
          implied. We do not warrant uninterrupted or error-free operation of our
          website.
        </p>
        <h3 className="terms-section__subtitle">9.2 Limitation of Liability</h3>
        <p>
          To the maximum extent permitted by law, {BRAND_NAME} shall not be liable for
          indirect, incidental, special, consequential, or punitive damages. Our total
          aggregate liability shall not exceed the amount you paid us in the 12 months
          preceding the claim.
        </p>
      </>
    ),
  },
  {
    id: 'indemnification',
    heading: '10. Indemnification',
    content: (
      <>
        <p>
          You agree to indemnify and hold harmless {BRAND_NAME} and its officers,
          directors, employees, and affiliates from any claims, damages, or expenses
          (including legal fees) arising from:
        </p>
        <ul className="terms-list">
          <li>Your use of our website or services</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any third-party rights</li>
          <li>Any content you submit or transmit through our website</li>
        </ul>
      </>
    ),
  },
  {
    id: 'termination',
    heading: '11. Termination',
    content: (
      <>
        <p>
          We may terminate or suspend your account immediately and without prior notice
          for any breach of these Terms or at our discretion.
        </p>
        <p>
          Upon termination your right to use our services ceases immediately. Provisions
          that survive — including IP rights, disclaimers, indemnification, and liability
          limitations — remain in full force.
        </p>
      </>
    ),
  },
  {
    id: 'governing',
    heading: '12. Governing Law and Dispute Resolution',
    content: (
      <>
        <h3 className="terms-section__subtitle">12.1 Governing Law</h3>
        <p>
          These Terms are governed by and construed in accordance with the laws of
          India, without regard to conflict-of-law provisions.
        </p>
        <h3 className="terms-section__subtitle">12.2 Dispute Resolution</h3>
        <p>
          Disputes shall first be addressed through good-faith negotiations. If
          unresolved, they shall be subject to the exclusive jurisdiction of the courts
          of India.
        </p>
      </>
    ),
  },
  {
    id: 'changes',
    heading: '13. Changes to Terms',
    content: (
      <>
        <p>
          We may modify these Terms at any time. Material changes will be posted on
          this page with an updated date and, where appropriate, communicated by email
          or in-app notice.
        </p>
        <p>
          We recommend reviewing this page periodically. Continued use after posting
          constitutes acceptance of the revised Terms.
        </p>
      </>
    ),
  },
];

// ── Component ──────────────────────────────────────────────────────────────────
export default function Terms() {
  return (
    <>
      {/* ── SEO Head ──────────────────────────────────────────────────────── */}
      <Helmet>
        <title>Terms of Service | {BRAND_NAME}</title>
        <meta
          name="description"
          content={`Read the Terms of Service for ${BRAND_NAME}. Covers purchases, returns, shipping, intellectual property, and your rights as a customer.`}
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={TERMS_URL} />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={TERMS_URL} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={`Terms of Service | ${BRAND_NAME}`} />
        <meta property="og:description" content={`Terms and conditions for shopping at ${BRAND_NAME}. Clear, fair, plain English.`} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`Terms of Service — ${BRAND_NAME}`} />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Terms of Service | ${BRAND_NAME}`} />
        <meta name="twitter:description" content={`Terms and conditions for shopping at ${BRAND_NAME}.`} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content={`Terms of Service — ${BRAND_NAME}`} />

        <script type="application/ld+json">{SCHEMA}</script>
      </Helmet>

      <Layout showFooter showBottomTab>
        {/*
          ┌─────────────────────────────────────────────────────────┐
          │  .terms-layout  →  100vw, full-bleed background layer   │
          │    .terms-page  →  max-width 900px, centred content     │
          │      .terms-hero  →  bleeds back to full-width via CSS  │
          │      .terms-cta   →  bleeds back to full-width via CSS  │
          └─────────────────────────────────────────────────────────┘
        */}
        <div className="terms-layout">
          <main className="terms-page" id="main-content">

            {/* ── Breadcrumb ──────────────────────────────────────────────── */}
            <nav className="terms-breadcrumb" aria-label="Breadcrumb">
              <ol className="terms-breadcrumb__list">
                <li>
                  <Link to="/" className="terms-breadcrumb__link">Home</Link>
                </li>
                <li className="terms-breadcrumb__sep" aria-hidden="true" />
                <li>
                  <span aria-current="page" className="terms-breadcrumb__current">
                    Terms of Service
                  </span>
                </li>
              </ol>
            </nav>

            {/* ── Hero ─────────────────────────────────────────────────────── */}
            <header className="terms-hero">
              <div className="terms-hero__icon" aria-hidden="true">
                <IconFileText size={48} />
              </div>
              <h1 className="terms-hero__title">Terms of Service</h1>
              <p className="terms-hero__subtitle">
                Written in plain English so you always know exactly where you stand.
              </p>
              <p className="terms-hero__updated">
                Last updated: <time dateTime={ISO_DATE}>{LAST_UPDATED}</time>
              </p>
            </header>

            {/* ── Policy Sections ─────────────────────────────────────────── */}
            <article className="terms-content" aria-label="Terms of service content">

              {SECTIONS.map(({ id, heading, content }) => (
                <section
                  key={id}
                  className="terms-section"
                  aria-labelledby={`${id}-heading`}
                >
                  <h2 id={`${id}-heading`} className="terms-section__title">
                    {heading}
                  </h2>
                  <div className="terms-section__content">
                    {content}
                  </div>
                </section>
              ))}

              {/* ── Contact — highlighted card ───────────────────────────── */}
              <section
                className="terms-section terms-section--highlight"
                aria-labelledby="contact-heading"
              >
                <h2 id="contact-heading" className="terms-section__title">
                  14. Contact Information
                </h2>
                <div className="terms-section__content">
                  <p>Questions about these Terms? Reach our legal team:</p>
                  <address className="terms-contact">
                    <p>
                      <strong>Email:</strong>{' '}
                      <a href={`mailto:${CONTACT_EMAIL}`} className="terms-link">
                        {CONTACT_EMAIL}
                      </a>
                    </p>
                    <p>
                      <strong>Contact Form:</strong>{' '}
                      <Link to="/contact" className="terms-link">
                        Visit our Contact page
                      </Link>
                    </p>
                    <p>
                      <strong>Registered Address:</strong> {BRAND_NAME}, India
                    </p>
                  </address>
                </div>
              </section>

            </article>

            {/* ── Related (internal linking) ────────────────────────────────── */}
            <nav className="terms-related" aria-label="Related information">
              <h2 className="terms-related__title">Related information</h2>
              <ul className="terms-related__list" role="list">
                <li><Link to="/privacy" className="terms-related__link">Privacy Policy</Link></li>
                <li><Link to="/cookies" className="terms-related__link">Cookies Policy</Link></li>
                <li><Link to="/contact" className="terms-related__link">Contact Us</Link></li>
                <li><Link to="/faq" className="terms-related__link">FAQ</Link></li>
              </ul>
            </nav>

            {/* ── CTA ──────────────────────────────────────────────────────── */}
            <section className="terms-cta" aria-labelledby="terms-cta-heading">
              <h2 id="terms-cta-heading" className="terms-cta__title">
                Have Questions About Our Terms?
              </h2>
              <p className="terms-cta__text">
                Our team is happy to clarify anything before you complete a purchase.
              </p>
              <div className="terms-cta__actions">
                <Link
                  to="/contact"
                  className="terms-cta__btn terms-cta__btn--primary"
                >
                  Contact Us
                  <IconArrowRight size={14} aria-hidden="true" />
                </Link>
                <Link
                  to="/privacy"
                  className="terms-cta__btn terms-cta__btn--ghost"
                >
                  View Privacy Policy
                  <IconArrowRight size={14} aria-hidden="true" />
                </Link>
              </div>
            </section>

          </main>
        </div>
      </Layout>
    </>
  );
}